import { Trash01 } from "@untitledui/icons"
import type { TeamMemberSummary } from "@/lib/api/types"
import { DesignDialog } from "@/components/ui/design-dialog"

function memberDisplayName(m: TeamMemberSummary) {
  const name = [m.firstName, m.lastName].filter(Boolean).join(" ").trim()
  return name || m.email
}

interface RemoveMemberDialogProps {
  member: TeamMemberSummary | null
  teamName: string
  loading: boolean
  onClose: () => void
  onConfirm: () => void
}

// Destructive confirmation for DELETE /v1/teams/:teamId/members/:userId.
// Backend: shipped (serverapi tracker #48). Surfaces the BE message verbatim
// on failure via the parent panel's toast — this dialog is presentation
// only.
export function RemoveMemberDialog({
  member,
  teamName,
  loading,
  onClose,
  onConfirm,
}: RemoveMemberDialogProps) {
  const open = !!member
  const name = member ? memberDisplayName(member) : ""
  return (
    <DesignDialog.Root open={open} onOpenChange={(v) => !v && onClose()}>
      <DesignDialog.Content width="md">
        <DesignDialog.Header>
          <DesignDialog.Title>Remove this person?</DesignDialog.Title>
          <DesignDialog.Description>
            <span className="font-semibold text-zinc-700">{name}</span> will lose
            access to{" "}
            <span className="font-semibold text-zinc-700">{teamName}</span> and
            won't see its estimates anymore. They can be re-invited later.
          </DesignDialog.Description>
        </DesignDialog.Header>
        <DesignDialog.Footer>
          <DesignDialog.Close asChild>
            <button
              type="button"
              className="h-12 px-5 rounded-xl text-[14px] font-semibold text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 transition-all duration-150"
            >
              Cancel
            </button>
          </DesignDialog.Close>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className="h-12 px-7 inline-flex items-center gap-2 rounded-xl bg-red-600 text-white text-[15px] font-semibold shadow-lg shadow-red-600/20 hover:bg-red-700 hover:shadow-red-600/30 transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
          >
            <Trash01 className="size-4" aria-hidden />
            {loading ? "Removing…" : "Remove from team"}
          </button>
        </DesignDialog.Footer>
      </DesignDialog.Content>
    </DesignDialog.Root>
  )
}
